import React, { useState, useEffect, useTransition, useDeferredValue } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, RefreshCw, LayoutGrid, List, UserPlus, Check, Briefcase, GraduationCap, Home, User, Building2 } from 'lucide-react';
import { getAllPeople } from '../../services/apiService';
import { getUserContacts, addPeopleAsContact, removeUserContact } from '../../services/userContactsService';
import PeopleCard from '../PeopleCard';
import { useTranslation } from '../../context/LanguageContext';

// Profile types mapping with icons and colors
const PROFILE_TYPES = {
    1: { code: 'STUDENT', labelFr: 'Étudiant', labelEn: 'Student', icon: GraduationCap, color: 'bg-blue-500' },
    3: { code: 'INDIVIDUAL', labelFr: 'Particulier', labelEn: 'Individual', icon: Home, color: 'bg-gray-500' },
    4: { code: 'ARTISAN', labelFr: 'Artisan', labelEn: 'Artisan', icon: Building2, color: 'bg-amber-500' },
    5: { code: 'PROFESSIONAL', labelFr: 'Professionnel', labelEn: 'Professional', icon: Briefcase, color: 'bg-indigo-500' },
    6: { code: 'RETIRED', labelFr: 'Pensionné', labelEn: 'Retired', icon: User, color: 'bg-teal-500' }
};

// Employment status mapping
const EMPLOYMENT_STATUS = {
    'JOBSEEKER': { labelFr: 'Demandeur d\'emploi', labelEn: 'Job Seeker', icon: Search, color: 'bg-green-500' },
    'EMPLOYER': { labelFr: 'Offreur d\'emploi', labelEn: 'Employer', icon: Briefcase, color: 'bg-orange-500' }
};

/**
 * Search contacts page - search entire directory and add contacts
 */
const SearchContacts = () => {
    const navigate = useNavigate();
    const { t, language } = useTranslation();
    const [people, setPeople] = useState([]);
    const [filteredPeople, setFilteredPeople] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [viewMode, setViewMode] = useState('cards'); // 'cards' or 'list'
    const [currentPage, setCurrentPage] = useState(1);
    const [userContacts, setUserContacts] = useState([]); // Array of contact objects
    const [contactPeopleIds, setContactPeopleIds] = useState(new Set()); // Set of createdById for quick lookup

    const [isPending, startTransition] = useTransition();
    const deferredSearchTerm = useDeferredValue(searchTerm);

    // Get user info from localStorage
    const userEmail = localStorage.getItem('userEmail') || '';
    const userId = parseInt(localStorage.getItem('userId'), 10);

    // Pagination
    const itemsPerPage = viewMode === 'cards' ? 10 : 20;
    const totalPages = Math.ceil(filteredPeople.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentPeople = filteredPeople.slice(startIndex, endIndex);

    // Load all people and user contacts on mount
    useEffect(() => {
        loadPeople();
        loadUserContacts();
    }, [userId]);

    const loadPeople = async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await getAllPeople({
                limit: 1000,
                sortBy: 'createdAt',
                sortOrder: 'DESC'
            });
            // Filter only PUBLIC contacts by default
            const publicPeople = data.filter(p => (p.role || p.ROLE) === 'PUBLIC');
            setPeople(publicPeople);
            setFilteredPeople(publicPeople);
        } catch (err) {
            console.error('Error loading people:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const loadUserContacts = async () => {
        if (!userId) return;
        try {
            // Force refresh from API (skip cache)
            const contacts = await getUserContacts(userId, true);
            console.log('Loaded contacts:', contacts);
            setUserContacts(contacts);
            // Build a Set of peopleuuid for quick lookup (peopleuuid = linked people UUID)
            const peopleUuids = new Set(contacts.map(c => c.peopleuuid).filter(Boolean));
            console.log('Contact peopleuuid Set:', [...peopleUuids]);
            setContactPeopleIds(peopleUuids);
        } catch (err) {
            console.error('Error loading user contacts:', err);
        }
    };

    // Filter people based on search term
    useEffect(() => {
        startTransition(() => {
            if (!deferredSearchTerm.trim()) {
                setFilteredPeople(people);
                setCurrentPage(1);
                return;
            }

            const searchLower = deferredSearchTerm.toLowerCase();
            const filtered = people.filter(person => {
                const firstName = (person.firstName || person.FIRSTNAME || '').toLowerCase();
                const lastName = (person.lastName || person.LASTNAME || '').toLowerCase();
                const email = (person.email || person.EMAIL || '').toLowerCase();
                const company = (person.company || person.COMPANY || '').toLowerCase();
                const phone = (person.phone || person.PHONE || '').toLowerCase();

                return firstName.includes(searchLower) ||
                    lastName.includes(searchLower) ||
                    email.includes(searchLower) ||
                    company.includes(searchLower) ||
                    phone.includes(searchLower);
            });

            setFilteredPeople(filtered);
            setCurrentPage(1);
        });
    }, [deferredSearchTerm, people]);

    const handleAddContact = async (person) => {
        const peopleUuid = person.peopleUuid || person.PEOPLEUUID;
        // addPeopleAsContact needs the full person object with peopleUuid
        const newContact = await addPeopleAsContact(userId, person);
        if (newContact) {
            setUserContacts(prev => [...prev, newContact]);
            setContactPeopleIds(prev => new Set([...prev, peopleUuid]));
        }
    };

    const handleRemoveContact = async (peopleUuid) => {
        // Find the contact by peopleuuid (the linked people UUID)
        const contact = userContacts.find(c => c.peopleuuid === peopleUuid);
        if (contact) {
            const success = await removeUserContact(userId, contact.id);
            if (success) {
                setUserContacts(prev => prev.filter(c => c.id !== contact.id));
                setContactPeopleIds(prev => {
                    const newSet = new Set(prev);
                    newSet.delete(peopleUuid);
                    return newSet;
                });
            }
        }
    };

    const handleToggleContact = async (person) => {
        const peopleUuid = person.peopleUuid || person.PEOPLEUUID;
        if (isContactAdded(peopleUuid)) {
            await handleRemoveContact(peopleUuid);
        } else {
            await handleAddContact(person);
        }
    };

    const isContactAdded = (peopleUuid) => {
        return contactPeopleIds.has(peopleUuid);
    };

    const handlePeopleClick = (person) => {
        const peopleUuid = person.peopleUuid || person.PEOPLEUUID;
        navigate(`/people/${peopleUuid}`);
    };

    // Get card styles based on role
    const getCardStyles = (role, email) => {
        const isCurrentUser = email === userEmail;

        if (isCurrentUser) {
            return {
                container: 'bg-gradient-to-br from-yellow-50 to-orange-50 border-2 border-yellow-400',
                badge: 'bg-yellow-500 text-white',
                title: 'text-yellow-700'
            };
        }

        switch (role) {
            case 'ADMIN':
                return {
                    container: 'bg-gradient-to-br from-pink-50 to-rose-50 border-2 border-cabaza-pink',
                    badge: 'bg-cabaza-pink text-white',
                    title: 'text-cabaza-pink'
                };
            case 'PUBLIC':
                return {
                    container: 'bg-gradient-to-br from-purple-50 to-pink-50 border-2 border-cabaza-purple',
                    badge: 'bg-gradient-to-r from-cabaza-purple to-cabaza-pink text-white',
                    title: 'text-cabaza-purple'
                };
            case 'PRIVATE':
            default:
                return {
                    container: 'bg-gray-50 border-2 border-gray-200',
                    badge: 'bg-gray-400 text-white',
                    title: 'text-gray-700'
                };
        }
    };

    const renderCardView = () => (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {currentPeople.map((person) => {
                const id = person.id || person.ID;
                const peopleUuid = person.peopleUuid || person.PEOPLEUUID;
                return (
                    <PeopleCard
                        key={id}
                        person={person}
                        onClick={handlePeopleClick}
                        actionType="add-remove"
                        onAction={() => handleToggleContact(person)}
                        isAdded={isContactAdded(peopleUuid)}
                        userEmail={userEmail}
                        language={language}
                        variant="dashboard"
                        t={t}
                    />
                );
            })}
        </div>
    );

    const renderListView = () => (
        <div className="space-y-2">
            {currentPeople.map((person) => {
                const id = person.id || person.ID;
                const peopleUuid = person.peopleUuid || person.PEOPLEUUID;
                const firstName = person.firstName || person.FIRSTNAME || '';
                const lastName = person.lastName || person.LASTNAME || '';
                const email = person.email || person.EMAIL || '';
                const phone = person.phone || person.PHONE || '';
                const company = person.company || person.COMPANY || '';
                const role = person.role || person.ROLE || 'PRIVATE';
                const profileTypeId = person.profileTypeId || person.profiletypeid || person.PROFILETYPEID || null;
                const employmentStatus = (person.employmentStatus || person.employmentstatus || person.EMPLOYMENTSTATUS || '').toUpperCase() || null;
                const styles = getCardStyles(role, email);
                const isAdded = isContactAdded(peopleUuid);
                const isCurrentUser = email === userEmail;

                return (
                    <div
                        key={id}
                        className={`${styles.container} rounded-lg p-4 hover:shadow-md transition-all cursor-pointer flex items-center justify-between`}
                    >
                        <div className="flex-1 min-w-0" onClick={() => handlePeopleClick(person)}>
                            <div className="flex items-center gap-4">
                                <div className="flex-1 min-w-0">
                                    <h3 className={`${styles.title} font-semibold truncate`}>
                                        {firstName} {lastName}
                                    </h3>
                                    <div className="flex items-center gap-4 text-sm text-gray-600 mt-1">
                                        {company && <span className="truncate">{company}</span>}
                                        {email && <span className="truncate">{email}</span>}
                                        {phone && <span>{phone}</span>}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center gap-2 ml-4 flex-wrap justify-end">
                            {/* Profile Type & Employment Status badges */}
                            {role === 'PUBLIC' && !isCurrentUser && (
                                <>
                                    {profileTypeId && PROFILE_TYPES[profileTypeId] && (
                                        <span className={`${PROFILE_TYPES[profileTypeId].color} text-white px-2 py-0.5 rounded text-[10px] font-medium flex items-center gap-1`}>
                                            {React.createElement(PROFILE_TYPES[profileTypeId].icon, { className: 'w-3 h-3' })}
                                            <span className="hidden sm:inline">{t?.profileTypes?.[PROFILE_TYPES[profileTypeId].code.toLowerCase()] || (language === 'fr' ? PROFILE_TYPES[profileTypeId].labelFr : PROFILE_TYPES[profileTypeId].labelEn)}</span>
                                        </span>
                                    )}
                                    {employmentStatus && EMPLOYMENT_STATUS[employmentStatus] && (
                                        <span className={`${EMPLOYMENT_STATUS[employmentStatus].color} text-white px-2 py-0.5 rounded text-[10px] font-medium flex items-center gap-1`}>
                                            {React.createElement(EMPLOYMENT_STATUS[employmentStatus].icon, { className: 'w-3 h-3' })}
                                            <span className="hidden sm:inline">{t?.employmentStatus?.[employmentStatus.toLowerCase()] || (language === 'fr' ? EMPLOYMENT_STATUS[employmentStatus].labelFr : EMPLOYMENT_STATUS[employmentStatus].labelEn)}</span>
                                        </span>
                                    )}
                                </>
                            )}
                            <span className={`${styles.badge} px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap`}>
                                {t?.badges?.[role.toLowerCase()] || role}
                            </span>
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleToggleContact(person);
                                }}
                                className={`p-2 rounded-full transition-colors ${
                                    isAdded
                                        ? 'bg-green-500 text-white hover:bg-green-600'
                                        : 'bg-white hover:bg-cabaza-purple hover:text-white text-cabaza-purple border-2 border-cabaza-purple'
                                }`}
                                title={isAdded ? (t.dashboard?.removeFromContacts || 'Remove from my contacts') : (t.dashboard?.addToContacts || 'Add to contacts')}
                            >
                                {isAdded ? <Check className="w-4 h-4" /> : <UserPlus className="w-4 h-4" />}
                            </button>
                        </div>
                    </div>
                );
            })}
        </div>
    );

    return (
        <div className="bg-white">
            {/* Search bar and view toggle */}
            <div className="mb-8 flex flex-col md:flex-row gap-4">
                <div className="relative flex-1">
                    <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-6 h-6" />
                    <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder={t.dashboard?.searchPlaceholder || 'Name, company, email...'}
                        className="w-full pl-14 pr-4 py-4 text-lg border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-cabaza-purple/20 focus:border-cabaza-purple transition-all"
                        autoFocus
                    />
                    {isPending && (
                        <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
                            <RefreshCw className="w-6 h-6 text-cabaza-purple animate-spin" />
                        </div>
                    )}
                </div>
                <div className="flex gap-2">
                    <button
                        onClick={() => setViewMode('cards')}
                        className={`p-4 rounded-xl border-2 transition-all ${
                            viewMode === 'cards'
                                ? 'border-cabaza-purple bg-cabaza-purple text-white'
                                : 'border-gray-200 text-gray-600 hover:border-cabaza-purple'
                        }`}
                        title={t.dashboard?.cardView || 'Card view'}
                    >
                        <LayoutGrid className="w-6 h-6" />
                    </button>
                    <button
                        onClick={() => setViewMode('list')}
                        className={`p-4 rounded-xl border-2 transition-all ${
                            viewMode === 'list'
                                ? 'border-cabaza-purple bg-cabaza-purple text-white'
                                : 'border-gray-200 text-gray-600 hover:border-cabaza-purple'
                        }`}
                        title={t.dashboard?.listView || 'List view'}
                    >
                        <List className="w-6 h-6" />
                    </button>
                </div>
            </div>

            {/* Loading state */}
            {loading && (
                <div className="text-center py-12">
                    <RefreshCw className="w-12 h-12 text-cabaza-purple mx-auto mb-4 animate-spin" />
                    <p className="text-gray-600 text-lg">
                        {t.dashboard?.loading || 'Loading...'}
                    </p>
                </div>
            )}

            {/* Error state */}
            {error && (
                <div className="bg-red-50 border-2 border-red-200 rounded-lg p-6 text-center max-w-md mx-auto">
                    <p className="text-red-800 mb-4">
                        {t.dashboard?.error || 'Error:'} {error}
                    </p>
                    <button
                        onClick={loadPeople}
                        className="bg-red-600 text-white px-6 py-2 rounded-lg hover:bg-red-700 transition-colors"
                    >
                        {t.dashboard?.retry || 'Retry'}
                    </button>
                </div>
            )}

            {/* Results */}
            {!loading && !error && (
                <>
                    {filteredPeople.length === 0 ? (
                        <div className="text-center py-12 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
                            <Search className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                            <p className="text-gray-500 text-lg">
                                {t.dashboard?.noContacts || 'No contact found'}
                            </p>
                        </div>
                    ) : (
                        <>
                            <div className="mb-4 flex justify-between items-center text-sm text-gray-500">
                                <span>
                                    {filteredPeople.length} {t.dashboard?.results || 'result(s)'}
                                </span>
                                <span>
                                    {t.dashboard?.page || 'Page'} {currentPage} / {totalPages}
                                </span>
                            </div>
                            <div style={{ opacity: isPending ? 0.6 : 1, transition: 'opacity 0.2s' }}>
                                {viewMode === 'cards' ? renderCardView() : renderListView()}
                            </div>

                            {/* Pagination */}
                            {totalPages > 1 && (
                                <div className="mt-8 flex justify-center gap-2">
                                    <button
                                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                        disabled={currentPage === 1}
                                        className="px-4 py-2 rounded-lg border-2 border-gray-200 text-gray-600 hover:border-cabaza-purple hover:text-cabaza-purple disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                    >
                                        {t.dashboard?.previous || 'Previous'}
                                    </button>
                                    <div className="flex items-center gap-1">
                                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                            let pageNum;
                                            if (totalPages <= 5) {
                                                pageNum = i + 1;
                                            } else if (currentPage <= 3) {
                                                pageNum = i + 1;
                                            } else if (currentPage >= totalPages - 2) {
                                                pageNum = totalPages - 4 + i;
                                            } else {
                                                pageNum = currentPage - 2 + i;
                                            }
                                            return (
                                                <button
                                                    key={pageNum}
                                                    onClick={() => setCurrentPage(pageNum)}
                                                    className={`w-10 h-10 rounded-lg border-2 transition-colors ${
                                                        currentPage === pageNum
                                                            ? 'border-cabaza-purple bg-cabaza-purple text-white'
                                                            : 'border-gray-200 text-gray-600 hover:border-cabaza-purple'
                                                    }`}
                                                >
                                                    {pageNum}
                                                </button>
                                            );
                                        })}
                                    </div>
                                    <button
                                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                        disabled={currentPage === totalPages}
                                        className="px-4 py-2 rounded-lg border-2 border-gray-200 text-gray-600 hover:border-cabaza-purple hover:text-cabaza-purple disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                    >
                                        {t.dashboard?.next || 'Next'}
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </>
            )}
        </div>
    );
};

export default SearchContacts;
